import { useState, useEffect } from 'react';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../lib/auth';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
}

interface Transaction {
  id: string;
  type: 'purchase' | 'deduction' | 'refund';
  amount: number;
  resulting_balance: number;
  reference_id: string | null;
  created_at: string;
}

export function Billing() {
  const { user, refreshUser } = useAuth();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [loadingTx, setLoadingTx] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [txPage, setTxPage] = useState(1);
  const [txTotalPages, setTxTotalPages] = useState(1);

  useEffect(() => {
    api
      .get<{ packages: CreditPackage[] }>('/credits/packages')
      .then((res) => setPackages(res.data?.packages ?? []))
      .catch(() => {})
      .finally(() => setLoadingPackages(false));
  }, []);

  useEffect(() => {
    setLoadingTx(true);
    api
      .get<{ items: Transaction[]; total: number }>('/users/me/transactions', {
        page: String(txPage),
        per_page: '20',
      })
      .then((res) => {
        setTransactions(res.data?.items ?? []);
        setTxTotalPages(Math.ceil((res.data?.total ?? 0) / 20));
      })
      .catch(() => {})
      .finally(() => setLoadingTx(false));
  }, [txPage]);

  const handlePurchase = async (packageId: string) => {
    setError('');
    setSuccess('');
    setPurchasing(packageId);
    try {
      await api.post('/credits/purchase', { package_id: packageId });
      setSuccess('Credits purchased successfully!');
      refreshUser();
      // Refresh transactions
      const res = await api.get<{ items: Transaction[]; total: number }>('/users/me/transactions', {
        page: '1',
        per_page: '20',
      });
      setTransactions(res.data?.items ?? []);
      setTxPage(1);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.error.message);
      } else {
        setError('Purchase failed');
      }
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Billing & Credits</h1>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">{success}</div>
      )}

      {/* Current Balance */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="text-sm text-gray-500">Current Balance</div>
        <div className="text-3xl font-bold text-gray-900">{user?.credit_balance ?? 0} credits</div>
      </div>

      {/* Credit Packages */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Credit Packages</h2>
        {loadingPackages ? (
          <p className="text-sm text-gray-500">Loading packages...</p>
        ) : packages.length === 0 ? (
          <p className="text-sm text-gray-500">No packages available</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                className="border border-gray-200 rounded-lg p-4 flex flex-col items-center text-center"
              >
                <h3 className="font-medium text-gray-900">{pkg.name}</h3>
                <div className="text-2xl font-bold text-blue-600 mt-2">{pkg.credits}</div>
                <div className="text-sm text-gray-500">credits</div>
                <div className="text-lg font-semibold text-gray-900 mt-2">
                  ${(pkg.price_cents / 100).toFixed(2)}
                </div>
                <button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing === pkg.id}
                  className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 min-h-[44px] transition-colors"
                >
                  {purchasing === pkg.id ? 'Processing...' : 'Purchase'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h2>
        {loadingTx ? (
          <p className="text-sm text-gray-500">Loading transactions...</p>
        ) : transactions.length === 0 ? (
          <p className="text-sm text-gray-500">No transactions yet</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Type</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Amount</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-600">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map((tx) => (
                    <tr key={tx.id}>
                      <td className="py-2 px-3 text-gray-700">
                        {new Date(tx.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            tx.type === 'purchase'
                              ? 'bg-green-100 text-green-700'
                              : tx.type === 'refund'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {tx.type}
                        </span>
                      </td>
                      <td
                        className={`py-2 px-3 text-right font-medium ${
                          tx.type === 'deduction' ? 'text-red-600' : 'text-green-600'
                        }`}
                      >
                        {tx.type === 'deduction' ? '-' : '+'}
                        {tx.amount}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-700">{tx.resulting_balance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {txTotalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button
                  onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                  disabled={txPage === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 min-h-[44px]"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {txPage} of {txTotalPages}
                </span>
                <button
                  onClick={() => setTxPage((p) => Math.min(txTotalPages, p + 1))}
                  disabled={txPage === txTotalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded text-sm disabled:opacity-50 min-h-[44px]"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
